import React, { useState, useEffect, useRef } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import ImageBoard from './components/ImageBoard';
import NotesSidebar from './components/NotesSidebar';
import ActionButtons from './components/ActionButtons';
import './App.css';

const { FiLink, FiImage, FiUpload, FiAlertCircle, FiMonitor, FiTablet, FiSmartphone } = FiIcons;

function App() {
  const [imageUrl, setImageUrl] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [selectedMarkerId, setSelectedMarkerId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(1920);
  const boardRef = useRef(null);

  const toBase64 = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

  useEffect(() => {
    const handlePaste = async (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          const blob = items[i].getAsFile();
          try {
            const base64 = await toBase64(blob);
            setImageUrl(base64);
            setMarkers([]);
            setError('');
          } catch (err) {
            setError('Kon geplakte afbeelding niet verwerken.');
          }
          break;
        }
      }
    };

    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, []);

  useEffect(() => {
    let timer;
    if (cooldown > 0) {
      timer = setInterval(() => {
        setCooldown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [cooldown]);

  const fetchScreenshot = async (e) => {
    e.preventDefault();
    if (!urlInput) return;

    if (cooldown > 0) {
      setError(`Snelheidslimiet: Wacht nog ${cooldown} seconden.`);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      let formattedUrl = urlInput.trim();
      if (!/^https?:\/\//i.test(formattedUrl)) {
        formattedUrl = 'https://' + formattedUrl;
      }

      const apiKey = import.meta.env.VITE_MARKUPGO_API_KEY;
      
      // Structure following provided documentation
      const requestBody = {
        source: {
          type: 'url',
          data: formattedUrl
        },
        options: {
          properties: {
            width: viewportWidth,
            format: 'jpeg',
            quality: 80,
          },
          waitDelay: '3s'
        }
      };

      const targetUrl = 'https://api.markupgo.com/api/v1/image';
      
      const response = await fetch(targetUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API Error ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      
      if (data && data.url) {
        const proxyUrl = `https://images.weserv.nl/?url=${encodeURIComponent(data.url)}&output=png`;
        
        const imgRes = await fetch(proxyUrl);
        if (!imgRes.ok) throw new Error('Kon afbeelding niet ophalen via proxy.');
        
        const blob = await imgRes.blob();
        const base64 = await toBase64(blob);
        
        setImageUrl(base64);
        setMarkers([]);
        setCooldown(60);
      } else {
        throw new Error('Geen afbeeldings-URL ontvangen van MarkupGo.');
      }

    } catch (err) {
      setError(`Screenshot mislukt: ${err.message}`);
      console.error('MarkupGo Error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      try {
        const base64 = await toBase64(file);
        setImageUrl(base64);
        setMarkers([]);
        setError('');
      } catch (err) {
        setError('Uploaden mislukt.');
      }
    }
  };

  const handleAddMarker = (x, y) => {
    const newMarker = {
      id: Date.now(),
      number: markers.length + 1,
      x,
      y,
      text: ''
    };
    setMarkers([...markers, newMarker]);
    setSelectedMarkerId(newMarker.id);
  };

  const updateMarkerText = (id, text) => {
    setMarkers(markers.map(m => m.id === id ? { ...m, text } : m));
  };

  const deleteMarker = (id) => {
    setMarkers(markers.filter(m => m.id !== id).map((m, index) => ({ ...m, number: index + 1 })));
  };

  const ViewportToggle = () => (
    <div className="flex bg-gray-100 p-1 rounded-xl border border-gray-200">
      <button
        type="button"
        onClick={() => setViewportWidth(1920)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${viewportWidth === 1920 ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
      >
        <SafeIcon icon={FiMonitor} /> Desktop
      </button>
      <button
        type="button"
        onClick={() => setViewportWidth(1024)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${viewportWidth === 1024 ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
      >
        <SafeIcon icon={FiTablet} /> Tablet
      </button>
      <button
        type="button"
        onClick={() => setViewportWidth(375)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${viewportWidth === 375 ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
      >
        <SafeIcon icon={FiSmartphone} /> Mobiel
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col font-sans">
      <header className="bg-white border-b border-gray-200 px-6 py-4 flex flex-col xl:flex-row items-center justify-between gap-4 z-10 shadow-sm">
        <div className="flex items-center gap-3 shrink-0">
          <img 
            src="https://wphelpdesk.nl/wp-content/themes/wphelpdesk/images/wphelpdesk-logo.svg" 
            alt="WPHelpdesk Logo" 
            className="h-10 w-auto"
          />
          <div className="hidden sm:block">
            <h1 className="text-xl font-bold text-gray-900 leading-tight">Screenshot tool</h1>
            <p className="text-xs text-gray-500">Notities & annotaties</p>
          </div>
        </div>

        <div className="flex flex-col md:flex-row items-center gap-4 w-full xl:justify-end">
          <ViewportToggle />
          
          <form onSubmit={fetchScreenshot} className="flex w-full md:w-96">
            <div className="relative flex-1">
              <SafeIcon icon={FiLink} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Website URL..."
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
              />
            </div>
            <button
              type="submit"
              disabled={isLoading || cooldown > 0}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-r-lg text-sm font-bold transition-colors disabled:opacity-70 whitespace-nowrap min-w-[110px]"
            >
              {isLoading ? 'Laden...' : cooldown > 0 ? `${cooldown}s` : 'Screenshot'}
            </button>
          </form>

          <div className="hidden md:block text-gray-300">|</div>

          <label className="cursor-pointer bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg text-sm font-bold transition-colors flex items-center gap-2 shadow-sm whitespace-nowrap">
            <SafeIcon icon={FiUpload} />
            <span>Upload</span>
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleImageUpload}
            />
          </label>
        </div>
      </header>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 m-6 mb-0 flex items-start gap-3 rounded-r-lg">
          <SafeIcon icon={FiAlertCircle} className="text-red-500 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <main className="flex-1 flex overflow-hidden">
        {imageUrl ? (
          <>
            <div className="flex-1 overflow-auto bg-gray-200 p-6 relative flex justify-center items-start">
              <ImageBoard
                imageUrl={imageUrl}
                markers={markers}
                onAddMarker={handleAddMarker}
                selectedMarkerId={selectedMarkerId}
                setSelectedMarkerId={setSelectedMarkerId}
                boardRef={boardRef}
                displayMode={viewportWidth === 375 ? 'mobile' : viewportWidth === 1024 ? 'tablet' : 'desktop'}
              />
            </div>
            <div className="w-96 bg-white border-l border-gray-200 flex flex-col shadow-xl z-10">
              <NotesSidebar
                markers={markers}
                updateMarkerText={updateMarkerText}
                deleteMarker={deleteMarker}
                selectedMarkerId={selectedMarkerId}
                setSelectedMarkerId={setSelectedMarkerId}
              />
              <ActionButtons boardRef={boardRef} markers={markers} />
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-400 p-8 text-center">
            <div className="bg-white p-10 rounded-3xl shadow-sm border border-gray-100 flex flex-col items-center max-w-lg">
              <div className="bg-blue-50 p-6 rounded-full mb-6">
                <SafeIcon icon={FiImage} className="text-5xl text-blue-400" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Start met annoteren</h2>
              <p className="text-gray-500 mb-8 leading-relaxed">
                Kies een breedte, voer een URL in en klik op Screenshot. Je kunt ook een afbeelding uploaden of direct plakken met <span className="font-mono bg-gray-100 px-1 rounded">Ctrl+V</span>.
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default App;